import React, { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    CircularProgress,
    Pagination,
    FormControl,
    Select,
    MenuItem,
    InputLabel,
    Grid,
    Chip,
    Stack,
    Fade,
    Grow,
    Avatar,
    Paper,
    Badge,
    alpha
} from '@mui/material';
import {
    EmojiEvents, AccessTime, Category, TrendingUp, Star,
    CheckCircle, LocalFireDepartment, Timeline, Insights
} from '@mui/icons-material';

const RewardHistoryItem = ({ reward, index }) => {
    const getEventTypeIcon = (eventType) => {
        switch (eventType) {
            case 'CLASSIFICATION':
                return '♻️';
            case 'LEVEL_UP':
                return '🆙';
            case 'BADGE_UNLOCK':
                return '🏆';
            case 'STREAK_BONUS':
                return '🔥';
            case 'DAILY_BONUS':
                return '📅';
            case 'MILESTONE':
                return '🎯';
            case 'ACCURACY_BONUS':
                return '🎯';
            case 'ACHIEVEMENT':
                return '🌟';
            case 'FEEDBACK_GIVEN':
                return '💬';
            default:
                return '⭐';
        }
    };

    const getEventTypeColor = (eventType) => {
        switch (eventType) {
            case 'CLASSIFICATION':
                return '#43a047';
            case 'LEVEL_UP':
                return '#ff9800';
            case 'BADGE_UNLOCK':
                return '#4caf50';
            case 'STREAK_BONUS':
                return '#f44336';
            case 'DAILY_BONUS':
                return '#2196f3';
            case 'MILESTONE':
                return '#9c27b0';
            case 'ACCURACY_BONUS':
                return '#2196f3';
            case 'ACHIEVEMENT':
                return '#ffd700';
            case 'FEEDBACK_GIVEN':
                return '#795548';
            default:
                return '#757575';
        }
    };

    const getEventTypeGradient = (eventType) => {
        const color = getEventTypeColor(eventType);
        return `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`;
    };

    const formatEventType = (eventType) => {
        return eventType?.replace('_', ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase()) || 'Reward';
    };

    const formatDateTime = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
        const diffMinutes = Math.floor(diffTime / (1000 * 60));

        if (diffDays > 0) {
            return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
        } else if (diffHours > 0) {
            return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
        } else if (diffMinutes > 0) {
            return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
        } else {
            return 'Just now';
        }
    };

    const getEventTitle = (reward) => {
        const titles = {
            'CLASSIFICATION': 'Eco Classification',
            'STREAK_BONUS': 'Streak Champion',
            'ACCURACY_BONUS': 'Precision Master',
            'MILESTONE': 'Milestone Achievement',
            'BADGE_UNLOCK': 'New Badge Earned',
            'DAILY_BONUS': 'Daily Check-in',
            'FEEDBACK_GIVEN': 'Community Helper',
            'ACHIEVEMENT': 'Achievement Unlocked',
            'LEVEL_UP': 'Level Up Bonus',
            'FIRST_CLASSIFICATION': 'Welcome Bonus',
        };
        return titles[reward.eventType] || reward.description || 'Eco Reward';
    };

    return (
        <Grow in={true} timeout={300 + (index * 100)}>
            <Card
                elevation={2}
                sx={{
                    borderRadius: 3,
                    border: `1px solid ${alpha(getEventTypeColor(reward.eventType), 0.1)}`,
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 20px ${alpha(getEventTypeColor(reward.eventType), 0.12)}`,
                        borderColor: alpha(getEventTypeColor(reward.eventType), 0.25)
                    },
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '3px',
                        height: '100%',
                        background: getEventTypeGradient(reward.eventType)
                    }
                }}
            >
                <CardContent sx={{ p: 2.5, pl: 3 }}>
                    <Stack direction="row" spacing={2.5} alignItems="center">
                        <Badge
                            badgeContent={reward.points > 50 ? '🔥' : null}
                            sx={{
                                '& .MuiBadge-badge': {
                                    fontSize: '10px',
                                    minWidth: 16,
                                    height: 16
                                }
                            }}
                        >
                            <Avatar
                                sx={{
                                    background: getEventTypeGradient(reward.eventType),
                                    width: 48,
                                    height: 48,
                                    fontSize: '1.4rem',
                                    boxShadow: `0 4px 12px ${alpha(getEventTypeColor(reward.eventType), 0.3)}`,
                                    border: '2px solid white'
                                }}
                            >
                                {getEventTypeIcon(reward.eventType)}
                            </Avatar>
                        </Badge>

                        <Box flex={1} sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5, color: '#1b5e20' }}>
                                {getEventTitle(reward)}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    mb: 1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical'
                                }}
                            >
                                {reward.description || 'Eco achievement unlocked!'}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ gap: 0.5 }}>
                                <Chip
                                    icon={<AccessTime />}
                                    label={formatDateTime(reward.earnedAt || reward.awardedAt)}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                        borderRadius: 2,
                                        height: 24,
                                        fontSize: '0.7rem',
                                        '& .MuiChip-icon': { fontSize: '0.9rem' }
                                    }}
                                />
                                <Chip
                                    label={formatEventType(reward.eventType)}
                                    size="small"
                                    sx={{
                                        background: alpha(getEventTypeColor(reward.eventType), 0.1),
                                        color: getEventTypeColor(reward.eventType),
                                        borderRadius: 2,
                                        fontWeight: 600,
                                        height: 24,
                                        fontSize: '0.7rem'
                                    }}
                                />
                                {reward.multiplier && reward.multiplier > 1 && (
                                    <Chip
                                        label={`${reward.multiplier}x`}
                                        size="small"
                                        sx={{
                                            background: 'linear-gradient(135deg, #ffc400, #ffeb3b)',
                                            color: '#bf360c',
                                            fontWeight: 700,
                                            borderRadius: 2,
                                            height: 24,
                                            fontSize: '0.7rem'
                                        }}
                                    />
                                )}
                            </Stack>
                        </Box>

                        <Paper elevation={2} sx={{
                            p: 1.5,
                            borderRadius: 2,
                            background: getEventTypeGradient(reward.eventType),
                            color: 'white',
                            minWidth: 80,
                            textAlign: 'center'
                        }}>
                            <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1 }}>
                                +{reward.points}
                            </Typography>
                            <Typography variant="caption" fontWeight={500} sx={{ opacity: 0.9, fontSize: '0.65rem' }}>
                                PTS
                            </Typography>
                        </Paper>
                    </Stack>
                </CardContent>
            </Card>
        </Grow>
    );
};

const RewardHistorySection = ({ history = [], loading = false }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortBy, setSortBy] = useState('newest');

    // Memoized calculations for performance - following RecentRewards.jsx pattern
    const { paginatedHistory, totalPages, totalPoints, sortedHistory } = useMemo(() => {
        // Sort the data based on selection
        let sortedData = [...history];
        if (sortBy === 'newest') {
            sortedData.sort((a, b) => new Date(b.earnedAt || b.awardedAt) - new Date(a.earnedAt || a.awardedAt));
        } else if (sortBy === 'oldest') {
            sortedData.sort((a, b) => new Date(a.earnedAt || a.awardedAt) - new Date(b.earnedAt || b.awardedAt));
        } else if (sortBy === 'highest') {
            sortedData.sort((a, b) => (b.points || 0) - (a.points || 0));
        } else if (sortBy === 'lowest') {
            sortedData.sort((a, b) => (a.points || 0) - (b.points || 0));
        }

        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const paginatedItems = sortedData.slice(startIndex, endIndex);
        const pages = Math.ceil(history.length / itemsPerPage);
        const points = history.reduce((sum, reward) => sum + (reward.points || 0), 0);

        return {
            paginatedHistory: paginatedItems,
            totalPages: pages,
            totalPoints: points,
            sortedHistory: sortedData
        };
    }, [history, currentPage, itemsPerPage, sortBy]);

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
        // Smooth scroll to top - same as RecentRewards.jsx
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(event.target.value);
        setCurrentPage(1); // Reset to first page
    };

    const handleSortChange = (event) => {
        setSortBy(event.target.value);
        setCurrentPage(1);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress size={60} sx={{ color: '#43a047', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                        Loading your eco achievements...
                    </Typography>
                </Box>
            </Box>
        );
    }

    if (history.length === 0) {
        return (
            <Card sx={{
                textAlign: 'center',
                py: 8,
                borderRadius: 4,
                background: 'linear-gradient(135deg, #f1f8e9 0%, #e8f5e8 100%)',
                border: '2px dashed #81c784'
            }}>
                <Avatar sx={{
                    fontSize: 80,
                    color: '#81c784',
                    background: 'transparent',
                    width: 120,
                    height: 120,
                    mx: 'auto',
                    mb: 3
                }}>
                    🌱
                </Avatar>
                <Typography variant="h4" fontWeight={700} sx={{ mb: 2, color: '#2e7d32' }}>
                    Start Your Eco Journey!
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
                    Classify waste to earn points and unlock amazing rewards
                </Typography>
                <Box sx={{
                    p: 3,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(67,160,71,0.1), rgba(102,187,106,0.1))',
                    border: '2px dashed rgba(67,160,71,0.3)',
                    maxWidth: 400,
                    mx: 'auto'
                }}>
                    <Typography variant="body1" color="text.secondary" fontWeight={600}>
                        🌱 Start classifying waste to earn points<br/>
                        🏆 Unlock badges and achievements<br/>
                        🔥 Build your daily streak<br/>
                        📈 Level up your eco impact!
                    </Typography>
                </Box>
            </Card>
        );
    }

    return (
        <Box>
            {/* Stats Header - Enhanced version from RecentRewards.jsx */}
            <Card elevation={6} sx={{
                mb: 4,
                borderRadius: 4,
                background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '200px',
                    height: '200px',
                    background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                    borderRadius: '50%',
                    transform: 'translate(50px, -50px)'
                }
            }}>
                <CardContent sx={{ p: 4, position: 'relative', zIndex: 1 }}>
                    <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h3" fontWeight={800}>
                                    {history.length.toLocaleString()}
                                </Typography>
                                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                                    Total Achievements
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h3" fontWeight={800}>
                                    {totalPoints.toLocaleString()}
                                </Typography>
                                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                                    Total Points Earned
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h3" fontWeight={800}>
                                    {Math.round(totalPoints / Math.max(history.length, 1))}
                                </Typography>
                                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                                    Average Per Achievement
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Enhanced Controls - Same pattern as RecentRewards.jsx */}
            <Box sx={{
                p: 4,
                mb: 4,
                background: 'rgba(255,255,255,0.95)',
                borderRadius: 4,
                border: '1px solid rgba(67, 160, 71, 0.1)',
                backdropFilter: 'blur(10px)'
            }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel sx={{ fontWeight: 600 }}>Sort By</InputLabel>
                        <Select
                            value={sortBy}
                            onChange={handleSortChange}
                            label="Sort By"
                            sx={{ borderRadius: 3 }}
                        >
                            <MenuItem value="newest">🕒 Newest First</MenuItem>
                            <MenuItem value="oldest">📅 Oldest First</MenuItem>
                            <MenuItem value="highest">💎 Highest Points</MenuItem>
                            <MenuItem value="lowest">📊 Lowest Points</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel sx={{ fontWeight: 600 }}>Per Page</InputLabel>
                        <Select
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                            label="Per Page"
                            sx={{ borderRadius: 3 }}
                        >
                            <MenuItem value={5}>5 rewards</MenuItem>
                            <MenuItem value={10}>10 rewards</MenuItem>
                            <MenuItem value={20}>20 rewards</MenuItem>
                            <MenuItem value={50}>50 rewards</MenuItem>
                        </Select>
                    </FormControl>

                    <Box flexGrow={1} />

                    <Stack direction="row" spacing={2} alignItems="center">
                        <Chip
                            icon={<Insights />}
                            label={`Showing ${((currentPage - 1) * itemsPerPage) + 1}-${Math.min(currentPage * itemsPerPage, history.length)} of ${history.length}`}
                            sx={{
                                background: alpha('#43a047', 0.1),
                                color: '#43a047',
                                fontWeight: 600,
                                border: `1px solid ${alpha('#43a047', 0.2)}`
                            }}
                        />
                    </Stack>
                </Stack>
            </Box>

            {/* Reward History Items */}
            <Fade in={true} timeout={600}>
                <Stack spacing={3} sx={{ mb: 4 }}>
                    {paginatedHistory.map((reward, index) => (
                        <RewardHistoryItem
                            key={`${reward.id || index}-${currentPage}`}
                            reward={reward}
                            index={index}
                        />
                    ))}
                </Stack>
            </Fade>

            {/* Enhanced Pagination - Same pattern as RecentRewards.jsx */}
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                    <Paper elevation={3} sx={{ borderRadius: 4, p: 2 }}>
                        <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={handlePageChange}
                            color="primary"
                            size="large"
                            showFirstButton
                            showLastButton
                            sx={{
                                '& .MuiPaginationItem-root': {
                                    fontWeight: 600,
                                    borderRadius: 3,
                                    '&:hover': {
                                        transform: 'translateY(-1px)',
                                        background: 'rgba(67,160,71,0.1)'
                                    }
                                },
                                '& .Mui-selected': {
                                    background: 'linear-gradient(135deg, #43a047, #66bb6a) !important',
                                    color: 'white !important',
                                    border: '2px solid #43a047 !important'
                                }
                            }}
                        />
                    </Paper>
                </Box>
            )}
        </Box>
    );
};

export default RewardHistorySection;
